import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Flex, Text, useDisclosure, VStack } from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import {
  AddCircleOutline,
  ArchiveOutline,
  DropdownInput,
  formatCurrency,
  formatNumber,
  recognitionService,
  StatCard,
  UserOctagonOutline,
  Arrow2Bold,
} from '@aslanpay/portal-common-lib';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import useAuthContext from 'app/hooks/useAuthContext';
import useGroups from 'app/hooks/useGroups';
import useGroupsWithPot from 'app/hooks/useGroupsWithPot/useGroupsWithPot';
import usePots from 'app/hooks/usePots';
import useFetchPointsTransactions from 'app/hooks/useFetchPointsTransactions';
import { walletAndPotsPaths } from 'app/constants/routes';
import getPointsValue from 'app/utils/points';
import { getGroupDisplayNameWithParents } from 'app/utils/groupsWithPot/groupsWithPot';
import { formatTimeAgo } from 'app/utils/date';
import { StrictGroupWithPot } from 'app/hooks/useGroupsWithPot/types';

import TransferPointsModal from 'app/components/Modals/TransferPointsModal/TransferPointsModal';
import TopUpPotModal from 'app/components/Modals/TopUpPotModal/TopUpPotModal';
import ArchivePotModal from 'app/components/Modals/ArchivePotModal/ArchivePotModal';
import AddPotManagerModal from 'app/components/Modals/AddPotManagerModal';
import PotTabCard from 'app/components/Pot/PotTabCard';
import PotEmployeesTable from 'app/components/Pot/PotEmployeesTable';
import PotManagersTable from 'app/components/Pot/PotManagersTable';
import PotActivityFeed from 'app/components/Pot/PotActivityFeed';

type ActiveTab = 'employees' | 'managers' | 'activity' | null;

function PotPage() {
  const navigate = useNavigate();
  const { company } = useAuthContext();
  const params = useParams();

  const potId = params.potId || '';
  const companyId = company?.id || '';

  const [activeTab, setActiveTab] = useState<ActiveTab>('employees');

  const {
    isOpen: isOpenTransferPointsModal,
    onClose: onCloseTransferPointsModal,
    onOpen: onOpenTransferPointsModal,
  } = useDisclosure();

  const {
    isOpen: isOpenTopUpPotModal,
    onClose: onCloseTopUpPotModal,
    onOpen: onOpenTopUpPotModal,
  } = useDisclosure();

  const {
    isOpen: isOpenArchivePotModal,
    onClose: onCloseArchivePotModal,
    onOpen: onOpenArchivePotModal,
  } = useDisclosure();

  const {
    isOpen: isOpenAddManagerModal,
    onClose: onCloseAddManagerModal,
    onOpen: onOpenAddManagerModal,
  } = useDisclosure();

  const { flatGroupsWithPots, isLoading: isLoadingGroupsWithPots } = useGroupsWithPot(
    companyId,
    !!companyId
  );

  const { data: pots, isLoading: isLoadingPot } = usePots.useFetchPots(
    {
      companyIds: companyId ? [companyId] : [],
      ids: potId ? [potId] : [],
      page: 0,
      size: 1,
    },
    !!companyId && !!potId
  );

  const pot = useMemo(() => pots?.list?.[0], [pots]);

  const { data: groups, isLoading: isLoadingGroup } = useGroups.useFetchGroups(
    {
      companyId,
      groupId: pot?.relatedId || '',
      page: 0,
      size: 1,
    },
    !!companyId && !!potId && !!pot?.relatedId && pot?.relatedType === 'GROUP'
  );

  const group = useMemo(() => groups?.list?.[0], [groups]);

  const groupWithPot: StrictGroupWithPot | undefined = useMemo(
    () => (group && pot ? { ...group, pot } : undefined),
    [group, pot]
  );

  const potOptions = useMemo(
    () =>
      flatGroupsWithPots.flatMap((g) =>
        g.pot ? [{ value: g.pot.id, text: getGroupDisplayNameWithParents(g) }] : []
      ),
    [flatGroupsWithPots]
  );

  const { data: latestActivityData } = useFetchPointsTransactions(
    { potIds: [potId], sort: 'DESC', sortBy: 'createdAt', size: 1, page: 0 },
    !!potId
  );

  const { data: latestRecognitionData } = useQuery({
    queryKey: ['latestRecognitionForGroup', group?.id],
    queryFn: () =>
      recognitionService.fetchRecognitions({
        groupIds: [group?.id ?? ''],
        statuses: ['APPROVED', 'REJECTED'],
        sort: 'DESC',
        sortBy: 'submittedAt',
        page: 0,
        size: 1,
      }),
    enabled: !!group?.id,
  });

  const latestActivityTimestamp = useMemo(() => {
    const txAt = latestActivityData?.list?.[0]?.createdAt;
    const recognition = latestRecognitionData?.list?.[0];
    const recognitionTimestamps = recognition
      ? [recognition.submittedAt, recognition.approvedAt, recognition.rejectedAt].filter(
          (v): v is string => !!v
        )
      : [];
    const candidates = [txAt, ...recognitionTimestamps].filter((v): v is string => !!v);
    if (candidates.length === 0) return 'No activity yet';
    const mostRecent = candidates.reduce((a, b) => (a > b ? a : b));
    return formatTimeAgo(mostRecent);
  }, [latestActivityData, latestRecognitionData]);

  const onChangePot = (value: string | number) => {
    navigate(walletAndPotsPaths.pot.path.replace(':potId', value.toString()));
  };

  const handleTabClick = (tab: ActiveTab) => {
    setActiveTab((prev) => (prev === tab ? null : tab));
  };

  if (isLoadingPot || isLoadingGroup || isLoadingGroupsWithPots) {
    return <CenterSpinner />;
  }

  if (!groupWithPot) {
    return null;
  }

  return (
    <>
      <AuthenticatedPageWrapper title="Wallets & Pots">
        <Flex flexDir="column" gap="30px">
          <Flex
            flexDir={{ base: 'column', md: 'row' }}
            justify={{ base: 'normal', md: 'space-between' }}
            gap="20px"
            alignItems={{ base: 'start', md: 'center' }}
          >
            <VStack spacing="5px" align="flex-start">
              <Text as="h2" textStyle="h2" color="brand.darkPurple">
                {groupWithPot.pot.name}{' '}
                <Text as="span" fontWeight="medium" color="brand.darkPurple">
                  Pot
                </Text>
              </Text>

              <Text as="p" textStyle="bodyRegular" color="brand.darkPurple">
                Pride: {groupWithPot.name}
              </Text>
            </VStack>

            <DropdownInput
              placeholder="Pot Selected"
              defaultValue={potOptions.find((option) => option.value === groupWithPot.pot.id)}
              options={potOptions}
              onChange={onChangePot}
              onBlur={() => {}}
              isSearchable
            />
          </Flex>

          <StatCard
            title="Points balance"
            value={formatNumber(groupWithPot.pot.balance)}
            description={`worth ${formatCurrency(getPointsValue(groupWithPot.pot.balance))}`}
          />

          <Flex
            direction="column"
            borderRadius="10px"
            border="1px solid"
            borderColor="brand.lightGray1"
            p="20px"
            shadow="lg"
            gap="28px"
          >
            <Text as="h5" textStyle="h5SemiBold" color="brand.darkPurple">
              Quick actions
            </Text>

            <Flex flexWrap="wrap" gap="20px" flex="1">
              <Button
                flex="1"
                minW="160px"
                size="lg"
                variant="outline"
                onClick={onOpenTopUpPotModal}
                leftIcon={<AddCircleOutline boxSize="24px" />}
              >
                Top-up pot
              </Button>
              <Button
                flex="1"
                minW="160px"
                size="lg"
                variant="outline"
                onClick={onOpenTransferPointsModal}
                leftIcon={<Arrow2Bold boxSize="24px" />}
              >
                Transfer points
              </Button>

              <Button
                flex="1"
                minW="160px"
                size="lg"
                variant="outline"
                onClick={onOpenAddManagerModal}
                leftIcon={<UserOctagonOutline boxSize="24px" />}
              >
                Add manager
              </Button>

              <Button
                flex="1"
                minW="160px"
                size="lg"
                variant="outline"
                onClick={onOpenArchivePotModal}
                leftIcon={<ArchiveOutline boxSize="24px" />}
              >
                Archive pot
              </Button>
            </Flex>
          </Flex>

          {/* Tab cards + active section — shared card */}
          <Flex
            flexDir="column"
            borderRadius="10px"
            border="1px solid"
            borderColor="brand.lightGray1"
            shadow="lg"
          >
            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              gap="16px"
              p="16px"
              borderBottom={activeTab ? '1px solid' : undefined}
              borderColor="brand.lightGray1"
            >
              <PotTabCard
                title="Members"
                description={
                  activeTab === 'employees' ? 'All members shown below' : 'Click to see members'
                }
                indicator={groupWithPot.memberCount + groupWithPot.managerCount}
                isActive={activeTab === 'employees'}
                onClick={() => handleTabClick('employees')}
              />
              <PotTabCard
                title="Pot Managers"
                description={
                  activeTab === 'managers'
                    ? 'All pot managers shown below'
                    : 'Click to see pot managers'
                }
                indicator={groupWithPot.managerCount}
                isActive={activeTab === 'managers'}
                onClick={() => handleTabClick('managers')}
              />
              <PotTabCard
                title="Last Activity"
                description={
                  activeTab === 'activity'
                    ? 'All activity shown below'
                    : 'Click to see full activity'
                }
                indicator={latestActivityTimestamp}
                isActive={activeTab === 'activity'}
                onClick={() => handleTabClick('activity')}
              />
            </Flex>

            {activeTab === 'employees' && (
              <Box p="20px">
                <PotEmployeesTable companyId={companyId} groupId={groupWithPot.id} />
              </Box>
            )}
            {activeTab === 'managers' && (
              <Box p="20px">
                <PotManagersTable
                  companyId={companyId}
                  groupId={groupWithPot.id}
                  flatGroupsWithPots={flatGroupsWithPots}
                />
              </Box>
            )}
            {activeTab === 'activity' && (
              <Box p="20px">
                <PotActivityFeed
                  potId={potId}
                  potName={groupWithPot.pot.name}
                  groupId={groupWithPot.id}
                />
              </Box>
            )}
          </Flex>
        </Flex>
      </AuthenticatedPageWrapper>

      <TransferPointsModal
        isOpen={isOpenTransferPointsModal}
        onClose={onCloseTransferPointsModal}
        originGroupWithPot={groupWithPot}
      />

      <ArchivePotModal
        isOpen={isOpenArchivePotModal}
        onClose={onCloseArchivePotModal}
        onSuccess={() => {
          navigate(walletAndPotsPaths.walletsAndPots.path);
        }}
        originGroupWithPot={groupWithPot}
        companyId={companyId}
      />

      <TopUpPotModal
        isOpen={isOpenTopUpPotModal}
        onClose={onCloseTopUpPotModal}
        originGroupWithPot={groupWithPot}
      />

      <AddPotManagerModal
        isOpen={isOpenAddManagerModal}
        onClose={onCloseAddManagerModal}
        originGroupWithPot={groupWithPot}
        companyId={companyId}
      />
    </>
  );
}

export default PotPage;
